/* Ruteo mínimo sobre la History API: el panel tiene ocho pantallas y no justifica una
 * dependencia. `useUbicacion` se re-renderiza con cada navegación y con atrás/adelante;
 * `Enlace` es un `<a>` de verdad (se abre en otra pestaña con ⌘/Ctrl) que navega sin
 * recargar. Se lo pasa como `as`/`linkAs` a los componentes del design system. */
import { forwardRef, useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";

const oyentes = new Set<() => void>();

function suscribir(f: () => void) {
  oyentes.add(f);
  window.addEventListener("popstate", f);
  return () => {
    oyentes.delete(f);
    window.removeEventListener("popstate", f);
  };
}

export function navegar(href: string, opciones: { reemplazar?: boolean } = {}) {
  const actual = location.pathname + location.search;
  if (href === actual) return;
  history[opciones.reemplazar ? "replaceState" : "pushState"](null, "", href);
  oyentes.forEach((f) => f());
  if (!opciones.reemplazar) window.scrollTo(0, 0);
}

export function useUbicacion() {
  const href = useSyncExternalStore(suscribir, () => location.pathname + location.search);
  return useMemo(() => {
    const url = new URL(href, location.origin);
    return { href, pathname: url.pathname, params: url.searchParams };
  }, [href]);
}

type PropsEnlace = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

export const Enlace = forwardRef<HTMLAnchorElement, PropsEnlace>(function Enlace({ href, onClick, target, ...resto }, ref) {
  const interno = href.startsWith("/") && !href.startsWith("/api/") && !href.startsWith("//");
  const alClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || !interno || target || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navegar(href);
  };
  return <a ref={ref} href={href} target={target} onClick={alClick} {...resto} />;
});
